import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  SETUP_STAGE_LABELS,
  type SetupItem,
  currentStage,
  markDone,
  markRitual,
  setupStages,
  skipItem,
  skipStage,
} from '@/lib/company-setup';
import { SetupAction } from '@/components/profile/setup/SetupAction';
import { SetupPipeline } from '@/components/profile/setup/SetupPipeline';
import { RitualRow } from '@/components/profile/setup/RitualRow';
import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { loadRitualsBySource } from '@/lib/workspace-rituals';
import type { WorkspaceRitual } from '@shared/types/workspace-rituals';
import type { CompanyQualificationSheet, SetupStage } from '@shared/types/company-qualification';

interface Props {
  sheet: CompanyQualificationSheet;
  connected: ReadonlySet<string>;
  /** Saves at once; resolves false when the save failed (the page says why). */
  onCommit: (next: CompanyQualificationSheet, note: string) => Promise<boolean>;
  busy: boolean;
}

/** One line of a stage: done or not, what it is, where it is done; skipped lines stay, greyed. */
const SetupRow = ({ item, connected, busy, onDone, onSkip }: {
  item: SetupItem; connected: ReadonlySet<string>; busy: boolean; onDone: (done: boolean) => void; onSkip: (skipped: boolean) => void;
}) => (
  <li className={cn('flex gap-3 border-t border-domino-cardBorder py-4 first:border-t-0', item.skipped && 'opacity-50')}>
    <input
      type="checkbox"
      checked={item.done}
      disabled={busy || item.detected || item.skipped}
      onChange={(event) => onDone(event.target.checked)}
      aria-label={`${item.title} : fait`}
      title={item.detected ? 'Constaté dans vos connexions' : undefined}
      className="mt-1 h-4 w-4 shrink-0 accent-domino-aubergine"
    />
    <div className="min-w-0 flex-1 space-y-2">
      <div>
        <p className={cn('text-sm font-medium leading-6 text-domino-ink', (item.done || item.skipped) && 'text-domino-inkSoft line-through')}>{item.title}</p>
        {item.detail && <p className="text-xs leading-5 text-domino-inkSoft">{item.detail}</p>}
      </div>
      {!item.done && !item.skipped && <div className="flex flex-wrap items-center gap-2"><SetupAction item={item} connected={connected} onApplied={() => onDone(true)} /></div>}
    </div>
    {!item.done && (
      <button type="button" onClick={() => onSkip(!item.skipped)} disabled={busy} className="shrink-0 self-start text-xs text-domino-inkSoft hover:text-domino-ink hover:underline">
        {item.skipped ? 'Reprendre' : 'Passer'}
      </button>
    )}
  </li>
);

/**
 * Step ③ « Mise en place » (13/10/2026): the analyst's proposals as a pipeline
 * of stages. The manager sees how far it goes, opens any stage, and may skip a
 * stage or a line; nothing is locked.
 */
export const SetupStep = ({ sheet, connected, onCommit, busy }: Props) => {
  const stages = setupStages(sheet, connected);
  const [selected, setSelected] = useState<SetupStage | null>(null);
  const { members, userId } = useWorkspaceObjectives();
  const [rituals, setRituals] = useState<Record<string, WorkspaceRitual>>({});
  const [ritualError, setRitualError] = useState<string | null>(null);
  useEffect(() => {
    void loadRitualsBySource().then(setRituals).catch((cause: unknown) => setRitualError(cause instanceof Error ? cause.message : 'Les rituels ne sont pas accessibles.'));
  }, []);
  const active = stages.find((view) => view.stage === selected) ?? stages.find((view) => view.stage === currentStage(stages));
  if (!active) return <p className="text-sm text-domino-inkSoft">Rien à mettre en place pour l’instant : l’analyse n’a rien proposé.</p>;
  const next = stages[stages.indexOf(active) + 1];
  const list = (item: SetupItem) => item.source.list;

  return (
    <div className="space-y-5">
      <SetupPipeline stages={stages} active={active.stage} onSelect={setSelected} />
      <section aria-label={SETUP_STAGE_LABELS[active.stage]} className="rounded-xl border border-domino-cardBorder bg-domino-warm px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-display text-xl font-semibold text-domino-ink">{SETUP_STAGE_LABELS[active.stage]}</h2>
          {active.status !== 'done' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                const skipping = active.status !== 'skipped';
                void onCommit(skipStage(sheet, active.stage, skipping), `${skipping ? 'Étape passée' : 'Étape reprise'} : ${SETUP_STAGE_LABELS[active.stage]}`)
                  .then((saved) => { if (saved && skipping && next) setSelected(next.stage); });
              }}
            >
              {active.status === 'skipped' ? 'Reprendre cette étape' : 'Passer cette étape'}
            </Button>
          )}
        </div>
        {ritualError && <p className="mt-2 text-sm text-red-700">{ritualError}</p>}
        {[
          { label: null, items: active.items.filter((item) => item.kind !== 'reflex') },
          { label: 'Ce que Domino fera pour vous', items: active.items.filter((item) => item.kind === 'reflex') },
        ].filter((group) => group.items.length > 0).map((group) => (
          <div key={group.label ?? 'main'}>
            {group.label && <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">{group.label}</h3>}
            <ul className="mt-2">
              {group.items.map((item) => (item.kind === 'ritual' ? (
                <RitualRow
                  key={item.id}
                  item={item}
                  ritual={rituals[item.id]}
                  members={members}
                  userId={userId}
                  busy={busy}
                  onAdded={(ritual) => {
                    setRituals((current) => ({ ...current, [item.id]: ritual }));
                    void onCommit(markRitual(sheet, item.id, ritual.id), `Rituel ajouté aux tâches : ${ritual.title}`);
                  }}
                  onRemoved={() => {
                    setRituals((current) => ({ ...current, [item.id]: { ...current[item.id]!, active: false } }));
                    void onCommit(markRitual(sheet, item.id, null), `Rituel retiré des tâches : ${item.title}`);
                  }}
                  onSkip={(skipped) => void onCommit(skipItem(sheet, 'usage', item.id, skipped), `${skipped ? 'Passé' : 'Repris'} : ${item.title}`)}
                />
              ) : (
                <SetupRow
                  key={item.id}
                  item={item}
                  connected={connected}
                  busy={busy}
                  onDone={(done) => void onCommit(markDone(sheet, list(item), item.id, done), `${done ? 'Fait' : 'À refaire'} : ${item.title}`)}
                  onSkip={(skipped) => void onCommit(skipItem(sheet, list(item), item.id, skipped), `${skipped ? 'Passé' : 'Repris'} : ${item.title}`)}
                />
              )))}
            </ul>
          </div>
        ))}
        {next && (
          <div className="mt-2 flex justify-end border-t border-domino-cardBorder pt-3">
            <Button size="sm" variant="secondary" onClick={() => setSelected(next.stage)}>{SETUP_STAGE_LABELS[next.stage]} →</Button>
          </div>
        )}
      </section>
    </div>
  );
};
